import { useRef } from 'react'
import { Camera, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface EtudiantAvatarProps {
  photoUrl: string | null
  prenom: string
  nom: string
  editable?: boolean
  onPhotoChange?: (file: File) => void
  onPhotoRemove?: () => void
  previewUrl?: string | null
}

export function EtudiantAvatar({ photoUrl, prenom, nom, editable, onPhotoChange, onPhotoRemove, previewUrl }: EtudiantAvatarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const displayUrl = previewUrl || photoUrl
  const initials = `${prenom?.[0] ?? ''}${nom?.[0] ?? ''}`.toUpperCase()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && onPhotoChange) {
      onPhotoChange(file)
    }
    // Reset input so the same file can be re-selected
    e.target.value = ''
  }

  return (
    <div className="relative inline-block">
      <div
        className={cn(
          'relative h-24 w-24 overflow-hidden rounded-full',
          editable && 'cursor-pointer',
        )}
        onClick={() => editable && fileInputRef.current?.click()}
      >
        {displayUrl ? (
          <img src={displayUrl} alt={`${prenom} ${nom}`} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary to-primary/70 text-2xl font-bold text-white">
            {initials}
          </div>
        )}

        {editable && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors hover:bg-black/30">
            <Camera className="h-6 w-6 text-white opacity-0 transition-opacity hover:opacity-100 [div:hover>&]:opacity-100" />
          </div>
        )}
      </div>

      {editable && displayUrl && onPhotoRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onPhotoRemove()
          }}
          className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-red-500 text-white shadow-sm transition-colors hover:bg-red-600"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}

      {editable && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      )}
    </div>
  )
}
